import { Platform } from "react-native";
import { config } from "../config";
import { trackPWAInstallation } from "./trackPWAInstallation";
import { FlickOColors } from "../components/shared/styles/color";

interface PWAConfig {
  restaurantId: string;
  scope: string;
  version?: string;
}

interface PWAParams extends PWAConfig {
  restaurantName: string;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export const registerPWA = async (pwaCfg: PWAConfig) => {
  console.log("registerPWA called, config: ", pwaCfg);
  if (Platform.OS !== "web" || !("serviceWorker" in navigator)) {
    //"serviceWorker" in navigator checks if the browser has service worker support
    console.log("serviceWorker not in navigator", Platform.OS);
    return;
  }
  const hostname = window.location.hostname;
  const restaurantName = hostname.split(".")[0];
  const id = pwaCfg.restaurantId;
  if (!id.length || !restaurantName.length) {
    console.log("id or name empty, ", id, restaurantName);
    return;
  }

  console.log("registerPWA hostname", hostname, `.${config.pwa.domain}`);
  const isSubdomain =
    hostname.includes(`.${config.pwa.domain}`) &&
    hostname !== `www.${config.pwa.domain}` &&
    hostname !== `${config.pwa.domain}`;

  // Only register PWA for subdomain access
  if (!isSubdomain) {
    console.log("no subdomain", isSubdomain);
    return;
  } else {
    console.log("subdomain found", isSubdomain);
  }

  try {
    const swUrl = `/common-sw.js?restId=${pwaCfg.restaurantId}&v=${
      pwaCfg.version || "v1"
    }`;
    console.log("swUrl: ", swUrl);
    const registration = await navigator.serviceWorker.register(swUrl, {
      scope: "/",
    });

    // Handle updates
    registration.addEventListener("updatefound", () => {
      console.log("updatefound addEventListener");
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          console.log("statechange addEventListener");
          if (newWorker.state === "installed") {
            // notifyUserOfUpdate();//TODO
          }
        });
      }
    });

    // Update manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      const pwaParam: PWAParams = { ...pwaCfg, restaurantName: restaurantName };
      const manifest = await generateManifest(pwaParam);
      const manifestBlob = new Blob([JSON.stringify(manifest)], {
        type: "application/json",
      });
      const manifestUrl = URL.createObjectURL(manifestBlob);
      manifestLink.setAttribute("href", manifestUrl);
      console.log("Generated manifest:", manifest);
    }

    // Handle PWA installation prompt
    const createInstallPrompt = (restName: string) => {
      const promptContainer = document.createElement("div");
      promptContainer.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: white;
        padding: 16px;
        box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
        z-index: 9999;
        display: flex;
        justify-content: space-between;
        align-items: center;
      `;

      const message = document.createElement("div");
      message.style.cssText = `
        font-size: 14px;
        color: #333;
      `;
      message.textContent = `Install ${restName} App for easier ordering`;

      const installButton = document.createElement("button");
      installButton.innerText = "Install";
      installButton.style.cssText = `
   background-color: ${FlickOColors.primary};
   color: ${FlickOColors.white};
   padding: 12px 24px;
   border-radius: 12px;
   font-weight: 600;
   border: none;
 `;

      const closeButton = document.createElement("button");
      closeButton.innerHTML = "&times;";
      closeButton.style.cssText = `
        position: absolute;
        top: 8px;
        right: 8px;
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
        color: #666;
      `;

      promptContainer.appendChild(message);
      promptContainer.appendChild(installButton);
      promptContainer.appendChild(closeButton);
      document.body.appendChild(promptContainer);

      return { promptContainer, installButton, closeButton };
    };

    // Handle PWA installation prompt
    window.addEventListener(
      "beforeinstallprompt",
      (e: BeforeInstallPromptEvent) => {
        console.log("beforeinstallprompt fired");
        if (window.matchMedia("(display-mode: standalone)").matches) {
          console.log("Already in standalone mode");
          return;
        }

        const { promptContainer, installButton, closeButton } =
          createInstallPrompt(restaurantName);

        installButton.onclick = async () => {
          try {
            const result: any = await e.prompt();
            console.log("Install prompt result:", result);

            if (result?.outcome === "accepted") {
              promptContainer.remove();
              trackPWAInstallation(pwaCfg.restaurantId);
            }
          } catch (error) {
            console.error("Install prompt error:", error);
            promptContainer.remove();
          }
        };

        closeButton.onclick = () => {
          promptContainer.remove();
        };
      }
    );
  } catch (error) {
    console.error("Service Worker registration failed:", error);
  }
};

// Generate dynamic manifest for each restaurant
export const generateManifest = async (pwaParam: PWAParams) => {
  const origin = window.location.origin;
  const cdnIcon512 = `https://cdn.flicko.online/rest/${pwaParam.restaurantId}/icon-512x512.png`;
  const cdnIcon144 = `https://cdn.flicko.online/rest/${pwaParam.restaurantId}/icon-144x144.png`;
  const fallbackIcon512 = `${origin}/public/default-icon-512-512.png`;
  const fallbackIcon144 = `${origin}/public/default-icon-144-144.png`;
  const deskScreenShot = `${origin}/public/pwa-desktop-1920-1080.png`;
  const mobileScreenShot = `${origin}/public/pwa-mobile-1080-1920.png`;

  // Check if CDN icon exists
  const checkImage = async (url: string) => {
    try {
      const img = new Image();
      const imageExists = await new Promise((resolve) => {
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
      });
      return imageExists;
    } catch (error) {
      console.warn(`Error checking image ${url}:`, error);
      return false;
    }
  };

  const exists512 = await checkImage(cdnIcon512);
  const exists144 = await checkImage(cdnIcon144);
  console.log("exists512: ", exists512, "exists144: ", exists144);
  // return checkImage(cdnIconUrl).then((exists) => {
  return {
    name: `${pwaParam.restaurantName}`, // Used on install screens, When users get the "Add to Home Screen" prompt in mobile browsers
    short_name: pwaParam.restaurantName, // Used under the icon
    description: `Digital menu for ${pwaParam.restaurantName}`,
    start_url: `${origin}/`,
    display: "standalone",
    background_color: "#ffffff",
    theme_color: "#000000",
    scope: origin + "/",
    icons: [
      {
        src: exists512 ? cdnIcon512 : fallbackIcon512,
        sizes: "512x512",
        type: "image/png",
        purpose: "any", // Add this line
      },
      {
        src: exists144 ? cdnIcon144 : fallbackIcon144,
        sizes: "144x144",
        type: "image/png",
        purpose: "any",
      },
    ],
    screenshots: [
      {
        src: deskScreenShot,
        sizes: "1920x1080",
        type: "image/png",
        form_factor: "wide",
      },
      {
        src: mobileScreenShot,
        sizes: "1080x1920",
        type: "image/png",
      },
    ],
  };
  // });
};
